import { getToken } from '../../services/tokenStore';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  //StepContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  // FormControl,
  // InputLabel,
  // Select,
  // MenuItem,
  Alert,
  Snackbar,
  IconButton,
  // Tooltip,
  Divider,
  Grid,
  // Card,
  // CardContent,
  // Badge,
  Tabs,
  Tab,
  // Accordion,
  // AccordionSummary,
  // AccordionDetails
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Comment as CommentIcon,
  CheckCircle as ApproveIcon,
  Send as SendIcon,
  //Person as PersonIcon,
  Group as GroupIcon,
  //Assignment as AssignmentIcon,
  History as HistoryIcon,
  // ExpandMore as ExpandMoreIcon,
  // Add as AddIcon,
  // Remove as RemoveIcon,
  // Flag as FlagIcon,
  AutoFixHigh as AIUpdateIcon,
  Save as SaveIcon,
  SwapHoriz as SwapHorizIcon
} from '@mui/icons-material';
//import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';
import DocumentEditor from '../../protocol/components/DocumentEditor';
//import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
//import { PDFViewer } from '@react-pdf/renderer';
import ProtocolPdfDocument from './ProtocolPdfDocument';
import PdfPreview from './PdfPreview';
import CommentSystem from './CommentSystem';
import { useAuth } from '../../context/AuthContext';
import ReassignPersonDialog from '../forms/ReassignPersonDialog';
import ZohoSignDialog from './ZohoSignDialog';
import { getStudyTeamByProject, reassignStudyTeamPerson } from '../../services/studyTeam.service';
import HighlightableDocument from './HighlightableDocument';
import HighlightablePdfOverlay from './HighlightablePdfOverlay';
import PdfJsHighlightViewer from './PdfJsHighlightViewer';
import PdfJsSimpleViewer from './PdfJsSimpleViewer';
import { startSpan, endSpan, emitEvent } from '../../telemetry/telemetry';

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

function isReviewer(user) {
  return user?.role === 'reviewer';
}

const ROLE_LABELS = {
  author_admins: 'Administrator',
  coordinators: 'Coordinator',
  co_authors: 'Co-Author',
  reviewers: 'Reviewer',
  approvers: 'Approver',
};

function normalizeEmailForIdentity(email) {
  return String(email || '').trim().toLowerCase();
}

/** Same identity-key shape used by StudyTeamForm/ReassignPersonDialog (userId, else email). */
function studyTeamPersonIdentityKeys(person) {
  const keys = new Set();
  if (person?.userId != null && person.userId !== '') keys.add(`uid:${String(person.userId)}`);
  const em = normalizeEmailForIdentity(person?.email);
  if (em) keys.add(`em:${em}`);
  return keys;
}

/**
 * The workflow's `coAuthors` list merges Study Team's `author_admins` + `co_authors`
 * (see applyStudyTeamToWorkflow on the server), so a person shown there could live in
 * either Study Team array. Search the given candidate role keys (in order) for the
 * person and return the role they actually belong to, so reassignment edits the right one.
 */
function findStudyTeamRoleForUser(studyTeamRoster, candidateRoleKeys, userId) {
  if (!studyTeamRoster || !userId) return null;
  const targetKey = `uid:${String(userId)}`;
  for (const role of candidateRoleKeys) {
    const list = Array.isArray(studyTeamRoster[role]) ? studyTeamRoster[role] : [];
    const idx = list.findIndex((p) => studyTeamPersonIdentityKeys(p).has(targetKey));
    if (idx !== -1) return { role, person: list[idx], list };
  }
  return null;
}

/** Workflow history entries store `user` as ObjectId or populated User — never coerce with String(object). */
function formatWorkflowHistoryUser(user) {
  if (user == null) return 'Unknown user';
  if (typeof user === 'string') return user;
  const first = user.firstName ?? '';
  const last = user.lastName ?? '';
  const full = `${first} ${last}`.trim();
  if (full) return full;
  if (user.email) return user.email;
  if (user.name) return user.name;
  const id = user._id ?? user.id;
  return id != null ? String(id) : 'Unknown user';
}

/** After removing legacy OnlyOffice tab, remap stored tab index down by one for tabs ≥ 2. */
function migrateWorkflowSelectedTab(saved) {
  const t = Number(saved);
  if (Number.isNaN(t)) return 0;
  let n = t;
  if (n >= 2) n -= 1;
  return Math.min(Math.max(n, 0), 3);
}

function uidStr(u) {
  if (u?.id != null) return String(u.id);
  if (u?._id != null) return String(u._id);
  return '';
}

/** Study-team roster membership (populated users or raw ids). */
function memberOfWorkflow(user, roster) {
  if (!user || !Array.isArray(roster)) return false;
  const id = uidStr(user);
  if (!id) return false;
  return roster.some((m) => String(m?._id ?? m) === id);
}

const getTodayDateInputMin = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const defaultDueDateInput = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

/** Assigned reviewers during review see only Comments (1) and Preview (3). */
const REVIEWER_TAB_INDICES = [1, 3];

const ASSIGN_DUE_DATE_CONFIG = {
  coAuthor: {
    title: 'Assign due date — Co-Authoring',
    description:
      'Set the date by which co-authors must complete their work. This date is included in notification emails.',
    confirmLabel: 'Send for Co-Authoring',
  },
  review: {
    title: 'Assign due date — Review',
    description:
      'Set the date by which reviewers must complete their review. This date is included in notification emails.',
    confirmLabel: 'Send for Review',
  },
  approval: {
    title: 'Assign due date — Approval',
    description:
      'Set the date by which approvers must complete sign-off. This date is included in notification emails.',
    confirmLabel: 'Send for Approval',
  },
};

const OnlyOfficeWorkflow = () => {
  const { workflowId } = useParams();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [documentData, setDocumentData] = useState(null);
  const [workflowState, setWorkflowState] = useState('draft');
  const [coAuthors, setCoAuthors] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [studyProjectId, setStudyProjectId] = useState(null);
  const [studyTeamRoster, setStudyTeamRoster] = useState(null);
  const [reassignState, setReassignState] = useState({ open: false, role: null, person: null });
  const [coAuthorProgress, setCoAuthorProgress] = useState([]);
  const [allCoAuthorsComplete, setAllCoAuthorsComplete] = useState(false);
  const [reviewProgress, setReviewProgress] = useState([]);
  const [approverProgress, setApproverProgress] = useState([]);
  const [allReviewsComplete, setAllReviewsComplete] = useState(false);
  const [allApproversMarked, setAllApproversMarked] = useState(false);
  const [approversAssigned, setApproversAssigned] = useState(0);
  const [comments, setComments] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [documentHistory, setDocumentHistory] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isNewDocument, setIsNewDocument] = useState(false);
  const onlyOfficeRef = useRef(null);
  const [onlyOfficeConfig, setOnlyOfficeConfig] = useState(null);
  const editorRef = useRef(null);
  const [exportedWordUrl, setExportedWordUrl] = useState(null);
  const documentEditorRef = useRef();
  const [publicDocxUrl, setPublicDocxUrl] = useState(null);
  const [pdfSections, setPdfSections] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const { user } = useAuth();
  const pdfProbeAbortRef = useRef(null);
  const [approveWithCommentsOpen, setApproveWithCommentsOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionComment, setRejectionComment] = useState('');
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [dueDateDialog, setDueDateDialog] = useState({ open: false, action: null, dueDate: '' });
  const [workflowDueDate, setWorkflowDueDate] = useState(null);
  // 21 CFR Part 11 §11.200 electronic signature — shared PIN-entry dialog for
  // every signing action (approve/reject/review-complete). `signAction.run`
  // is the actual fetch, deferred until the PIN is entered and confirmed.
  const [signAction, setSignAction] = useState(null); // { meaning, run: (signSessionId) => Promise<void> }
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [signError, setSignError] = useState('');

  const availableUsersById = useMemo(() => {
    const entries = (availableUsers || []).map(u => [u?.id ?? u?._id, u]).filter(([id]) => !!id);
    return new Map(entries);
  }, [availableUsers]);

  const resolveUser = (maybeUser) => {
    if (!maybeUser) return null;
    if (typeof maybeUser === 'string') {
      return availableUsersById.get(maybeUser) || { id: maybeUser };
    }
    return maybeUser;
  };

  const getUserFullName = (maybeUser) => {
    const u = resolveUser(maybeUser);
    if (!u) return 'Unknown User';
    if (typeof u?.name === 'string' && u.name.trim()) return u.name.trim();
    const first = (u.firstName || '').toString().trim();
    const last = (u.lastName || '').toString().trim();
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    if (u.email) return u.email;
    if (u.username) return u.username;
    if (u.id) return `User ${u.id.toString().slice(-6)}`;
    if (u._id) return `User ${u._id.toString().slice(-6)}`;
    return 'Unknown User';
  };

  const getUserInitials = (maybeUser) => {
    const full = getUserFullName(maybeUser);
    if (!full) return 'U';
    const parts = full
      .replace(/[^a-zA-Z0-9@\s._-]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) {
      const p = parts[0];
      const emailPrefix = p.includes('@') ? p.split('@')[0] : p;
      return (emailPrefix[0] || 'U').toUpperCase();
    }
    return `${parts[0][0] || 'U'}${parts[1][0] || ''}`.toUpperCase();
  };

  // Backend URL for OnlyOffice iframe
  //const backendUrl = process.env.REACT_APP_API_URL || 'https://backend-clinical-intake.dizzaroo.com';

  // Move these function definitions to the top of the component, before any useEffect or code that calls them
  const loadDocumentData = async () => {
    if (!documentId || documentId === 'new') {
      return;
    }
    try {
      setLoading(true);
      const token = getToken();
      // API call to load document data
      const response = await fetch(`${API_BASE_URL}/workflows/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setDocumentData(data);
    } catch (error) {
      console.error('Error loading document:', error);
      showSnackbar('Error loading document', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowState = async () => {
    if (!documentId || documentId === 'new') {
      return;
    }
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setWorkflowState(data.state);
      setCoAuthors(data.coAuthors || []);
      setReviewers(data.reviewers || []);
      setComments(data.comments || []);
      setCoordinators(data.coordinators || []);
      setApprovers(data.approvers || []);
      setCoAuthorProgress(data.coAuthorProgress || []);
      setAllCoAuthorsComplete(Boolean(data.allCoAuthorsComplete));
      setReviewProgress(data.reviewProgress || []);
      setApproverProgress(data.approverProgress || []);
      setAllReviewsComplete(Boolean(data.allReviewsComplete));
      setAllApproversMarked(Boolean(data.allApproversMarked));
      setApproversAssigned(Number(data.approversAssigned) || (data.approvers || []).length || 0);
      setDocumentHistory(data.history || []);
      setWorkflowDueDate(data.dueDate || null);
      setStudyProjectId(data.projectId || null);
    } catch (error) {
      console.error('Error loading workflow state:', error);
    }
  };

  const loadStudyTeamRoster = async () => {
    if (!studyProjectId) return;
    try {
      const res = await getStudyTeamByProject(studyProjectId);
      setStudyTeamRoster(res?.data || null);
    } catch (error) {
      console.error('Error loading study team roster:', error);
      setStudyTeamRoster(null);
    }
  };

  useEffect(() => {
    loadStudyTeamRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyProjectId]);

  /**
   * Open the shared reassign dialog for a person shown in the sidebar. `candidateRoleKeys`
   * disambiguates the Co-Authors list, which merges Study Team's author_admins + co_authors —
   * the person could be in either array.
   */
  const openReassignDialog = (rawPerson, candidateRoleKeys) => {
    const resolved = resolveUser(rawPerson);
    const userId = resolved?.id || resolved?._id;
    const match = findStudyTeamRoleForUser(studyTeamRoster, candidateRoleKeys, userId);
    if (!match) {
      showSnackbar(
        'Could not find this person in the Study Team roster for this role — reassign from the Study Team tab instead.',
        'error'
      );
      return;
    }
    setReassignState({ open: true, role: match.role, person: match.person });
  };

  const closeReassignDialog = () => setReassignState((s) => ({ ...s, open: false }));

  const handleReassignConfirm = async (newPerson) => {
    const { role, person } = reassignState;
    const body = await reassignStudyTeamPerson(studyProjectId, { role, oldPerson: person, newPerson });
    closeReassignDialog();
    setStudyTeamRoster(body?.data || null);
    await loadWorkflowState();
    const synced = typeof body?.syncedWorkflows === 'number' ? body.syncedWorkflows : 0;
    showSnackbar(
      `Reassigned ${ROLE_LABELS[role]}.${synced > 0 ? ` Synced to ${synced} document workflow${synced === 1 ? '' : 's'}.` : ''}`,
      'success'
    );
  };

  const reassignOccupiedKeys = useMemo(() => {
    if (!reassignState.role || !studyTeamRoster) return new Set();
    const list = Array.isArray(studyTeamRoster[reassignState.role]) ? studyTeamRoster[reassignState.role] : [];
    const keys = new Set();
    for (const p of list) {
      if (p === reassignState.person) continue;
      studyTeamPersonIdentityKeys(p).forEach((k) => keys.add(k));
    }
    return keys;
  }, [reassignState.role, reassignState.person, studyTeamRoster]);

  // Fetch workflow details by workflowId
  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        startSpan('ui_workflow_page_load', { page: 'workflow' });
        setLoading(true);
        setError(null);
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/workflows/workflow/${workflowId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to fetch workflow: ${res.status} ${text}`);
    }
        const data = await res.json();
        setWorkflow(data);
        await endSpan('ui_workflow_page_load', { status: 'ok' });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (workflowId) fetchWorkflow();
  }, [workflowId]);

  // Fetch OnlyOffice config when workflow is loaded
  useEffect(() => {
    const fetchOnlyOfficeConfig = async () => {
      if (!workflow?.documentId?._id && !workflow?.documentId) return;
      const documentId = workflow.documentId._id || workflow.documentId;
      startSpan('ui_onlyoffice_config_fetch', { page: 'workflow' });
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/workflows/${documentId}/onlyoffice-editor`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setOnlyOfficeConfig(data.config);
      await endSpan('ui_onlyoffice_config_fetch', { status: res.ok ? 'ok' : 'error' });
    };
    if (workflow && (workflow.documentId?._id || workflow.documentId)) {
      fetchOnlyOfficeConfig();
    }
  }, [workflow]);

  // Initialize OnlyOffice editor when config is ready
  useEffect(() => {
    if (onlyOfficeConfig && window.DocsAPI && editorRef.current) {
      startSpan('ui_onlyoffice_editor_init', { page: 'workflow' });
      // Clean up previous editor if any
      editorRef.current.innerHTML = '';
      new window.DocsAPI.DocEditor(editorRef.current.id, onlyOfficeConfig);
      endSpan('ui_onlyoffice_editor_init', { status: 'ok' });
      emitEvent({ metric: 'ui_onlyoffice_editor_ready', page: 'workflow', status: 'ok' });
    }
  }, [onlyOfficeConfig]);

  // Extract documentId as a string
  const documentId = workflow?.documentId?._id || workflow?.documentId;
  const document = workflow?.documentId && (workflow.documentId.title ? workflow.documentId : null);

  // Handle new document or load document/workflow state
  useEffect(() => {
    if (documentId === 'new') {
      setIsNewDocument(true);
      setDocumentData({
        title: 'New Document',
        type: 'protocol',
        status: 'draft'
      });
    } else if (documentId) {
      setIsNewDocument(false);
      loadDocumentData();
      loadWorkflowState();
    }
  }, [documentId]);

   /**
   * Comments-only tabs (hide Project Section Editor + Save) only when:
   * workflow is in review, user is an assigned reviewer, and they are not also a co-author or coordinator.
   * Do not use global `isReviewer(role)` here — that hid the editor for every account with reviewer permissions
   * (including coordinators) during co-authoring, so Save never appeared.
   */
  const workflowReviewerMode =
    workflowState === 'reviewing' &&
    memberOfWorkflow(user, reviewers) &&
    !memberOfWorkflow(user, coAuthors) &&
    !memberOfWorkflow(user, coordinators);

  const guidanceRole = useMemo(() => {
    if (workflowState === 'pendingApproval') return 'approver';
    if (workflowState === 'reviewing') return 'reviewer';
    if (workflowState === 'coAuthoring') return 'coAuthor';
    return 'author';
  }, [workflowState]);

  // 1. Function to map workflow state to step index
  const getStepFromWorkflowState = (state) => {
    switch (state) {
      case 'draft': return 0;
      case 'coAuthoring': return 1;
      case 'reviewing': return 2;
      case 'pendingApproval': return 3;
      case 'approved': return 3;
      default: return 0;
    }
  };

  // 2. useEffect to sync activeStep with workflowState
  useEffect(() => {
    if (workflowState) {
      const stepIndex = getStepFromWorkflowState(workflowState);
      setActiveStep(stepIndex);
    }
  }, [workflowState]);

  // 3. Steps array (no status field needed for Stepper)
  const steps = [
    { label: 'Draft Creation', description: 'Create initial document draft' },
    { label: 'Co-Authoring', description: 'Collaborative editing with multiple authors' },
    { label: 'Review & Comment', description: 'Reviewers add comments and suggestions' },
    { label: 'Final Approval', description: 'Document approval and preview' }
  ];

  // Workflow states
  const workflowStates = {
    draft: { label: 'Draft', color: 'default', icon: EditIcon },
    coAuthoring: { label: 'Co-Authoring', color: 'primary', icon: GroupIcon },
    reviewing: { label: 'Reviewing', color: 'warning', icon: CommentIcon },
    pendingApproval: { label: 'Pending approval', color: 'info', icon: ApproveIcon },
    aiUpdating: { label: 'AI Updating', color: 'info', icon: AIUpdateIcon },
    approved: { label: 'Approved', color: 'success', icon: ApproveIcon }
  };

  // Add this function to fetch available users
  // const loadAvailableUsers = async () => {
  //   try {
  //     const token = getToken();
  //     const response = await fetch('https://backend-clinical-intake.dizzaroo.com/api/workflows/users/available', {
  //       headers: {
  //         'Authorization': `Bearer ${token}`
  //       }
  //     });
  //     if (response.ok) {
  //       const data = await response.json();
  //       setAvailableUsers(data.users || []);
  //     }
  //   } catch (error) {
  //     console.error('Error loading users:', error);
  //   }
  // };

  // // Place this useEffect BEFORE any return statement
  useEffect(() => {
    const savedTab = localStorage.getItem(`selectedTab-${documentId}`);
    if (savedTab !== null) setSelectedTab(migrateWorkflowSelectedTab(savedTab));
  }, [documentId]);

  // Persist selectedTab in localStorage
  // Reviewers cannot use Project Section Editor tab
  useEffect(() => {
     if (workflowReviewerMode && !REVIEWER_TAB_INDICES.includes(selectedTab)) {
      setSelectedTab(1);
    }
  }, [user, selectedTab, workflowReviewerMode]);

  useEffect(() => {
    if (documentId) localStorage.setItem(`selectedTab-${documentId}`, selectedTab);
  }, [selectedTab, documentId]);

  /** Ask server if an exported PDF exists; updates pdfUrl. Aborts stale probes when documentId changes or a new probe starts. */
  const refreshPdfAvailability = useCallback(async () => {
    if (!documentId || documentId === 'new') return false;
    pdfProbeAbortRef.current?.abort();
    const ac = new AbortController();
    pdfProbeAbortRef.current = ac;
      const token = getToken();
    try {  
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/pdf`, {
        method: 'HEAD',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: ac.signal,
      });
      const ok = response.ok;
      if (!ac.signal.aborted) {
        if (ok) {
        setPdfUrl(`${API_BASE_URL}/documents/${documentId}/pdf`);
      } else {
        setPdfUrl(null);
      }
       }
      return ok;
    } catch (e) {
      if (e.name === 'AbortError') return false;
      if (!ac.signal.aborted) setPdfUrl(null);
      return false;
    }
  }, [documentId]);

   useEffect(() => {
    refreshPdfAvailability();
    return () => pdfProbeAbortRef.current?.abort();
  }, [documentId, refreshPdfAvailability]);

  if (loading) return <div>Loading workflow details...</div>;
  if (error) return <div style={{color:'red'}}>Error: {error}</div>;
  if (!workflow) return <div>No workflow found.</div>;

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // const openUserDialog = (type) => {
  //   setDialogType(type);
  //   setOpenDialog(true);
  // };

  // const handleUserSelection = async () => {
  //   if (!documentId || documentId === 'new') {
  //     showSnackbar('Please create a document first', 'warning');
  //     return;
  //   }
  //   try {
  //     const token = getToken();
  //     const endpoint = dialogType === 'coAuthors' ? 'co-authors' : 'reviewers';
  //     const response = await fetch(`https://backend-clinical-intake.dizzaroo.com/api/workflows/${documentId}/${endpoint}`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${token}`
  //       },
  //       body: JSON.stringify({ users: selectedUsers })
  //     });
  //     if (response.ok) {
  //       if (dialogType === 'coAuthors') {
  //         setCoAuthors(selectedUsers.map(resolveUser));
  //       } else {
  //         setReviewers(selectedUsers.map(resolveUser));
  //       }
  //       setOpenDialog(false);
  //       setSelectedUsers([]);
  //       showSnackbar(`${dialogType === 'coAuthors' ? 'Co-authors' : 'Reviewers'} assigned successfully`);
  //     }
  //   } catch (error) {
  //     console.error('Error assigning users:', error);
  //     showSnackbar('Error assigning users', 'error');
  //   }
  // };

  const openDueDateDialog = (action) => {
    setDueDateDialog({
      open: true,
      action,
      dueDate: defaultDueDateInput(),
    });
  };

  const closeDueDateDialog = () => {
    setDueDateDialog({ open: false, action: null, dueDate: '' });
  };

  const postCoordinatorWorkflowAction = async (endpoint, dueDate) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ dueDate }),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
  };

  const sendForCoAuthoring = async (dueDate) => {
    if (!documentId || documentId === 'new') {
      showSnackbar('Please create a document first', 'warning');
      return;
    }
    if (!dueDate) {
      openDueDateDialog('coAuthor');
      return;
    }
    try {
      setLoading(true);
      // const token = getToken();
      // const response = await fetch(`https://backend-clinical-intake.dizzaroo.com/api/workflows/${documentId}/workflow/co-authoring`, {
      //   method: 'POST',
      //   headers: {
      //     'Authorization': `Bearer ${token}`
      //   }
      // });
      // if (response.ok) {

        const { ok, data } = await postCoordinatorWorkflowAction('co-authoring', dueDate);
      if (ok) {
        setWorkflowState('coAuthoring');
        setWorkflowDueDate(data.dueDate || dueDate);
        showSnackbar('Document sent for co-authoring');
        closeDueDateDialog();
        handleNext();
        await loadWorkflowState();
      } else {
        showSnackbar(data.message || 'Error sending for co-authoring', 'error');
      }
    } catch (error) {
      console.error('Error sending for co-authoring:', error);
      showSnackbar('Error sending for co-authoring', 'error');
    } finally {
      setLoading(false);
    }
  };

  const sendForReview = async (dueDate) => {
    if (!documentId || documentId === 'new') {
      showSnackbar('Please create a document first', 'warning');
      return;
    }
    if (!dueDate) {
      openDueDateDialog('review');
      return;
    }
    const hasPdf = await refreshPdfAvailability();
    if (!hasPdf) {
      showSnackbar(
        'No exported PDF on the server yet. Open Project Section Editor and click Save (export PDF) before sending for review.',
        'warning'
      );
      setSelectedTab(0);
      return;
    }
    try {
      setLoading(true);
    //   const token = getToken();
    //   const response = await fetch(`https://backend-clinical-intake.dizzaroo.com/api/workflows/${documentId}/workflow/review`, {
    //     method: 'POST',
    //     headers: {
    //       'Authorization': `Bearer ${token}`
    //     }
    //   });
    //  if (response.ok) {
      
       const { ok, data } = await postCoordinatorWorkflowAction('review', dueDate);
      if (ok) {
        setWorkflowState('reviewing');
        setWorkflowDueDate(data.dueDate || dueDate);
        showSnackbar('Document sent for review');
        closeDueDateDialog();
        await refreshPdfAvailability();
        setSelectedTab(1);
        await loadWorkflowState();
      } else {
        showSnackbar(data.message || 'Error sending for review', 'error');
      }
    } catch (error) {
      console.error('Error sending for review:', error);
      showSnackbar('Error sending for review', 'error');
    } finally {
      setLoading(false);
    }
  };

  /** Coordinator finalizes after all approvers recorded (exports PDF when editor supports it). */
  const coordinatorFinalizeDocumentApproval = async () => {
    if (!documentId || documentId === 'new') {
      showSnackbar('Please create a document first', 'warning');
      return;
    }
    try {
      setLoading(true);

      // Export and upload the latest PDF before approving
      if (documentEditorRef.current && documentEditorRef.current.exportToPdf) {
        startSpan('ui_workflow_export_pdf_before_approve', { page: 'workflow', meta: { documentId } });
        const pdfBlob = await documentEditorRef.current.exportToPdf();
        const formData = new FormData();
        formData.append('pdf', pdfBlob, 'document.pdf');
        const token = getToken();
        startSpan('ui_workflow_upload_pdf', { page: 'workflow', meta: { documentId } });
        const uploadResponse = await fetch(`${API_BASE_URL}/documents/${documentId}/upload-pdf`, {
          method: 'POST',
          body: formData,
          headers: { Authorization: `Bearer ${token}` }
        });
        if (uploadResponse.ok) {
          setPdfUrl(`${API_BASE_URL}/documents/${documentId}/pdf`);
          endSpan('ui_workflow_upload_pdf', { status: 'ok' });
          endSpan('ui_workflow_export_pdf_before_approve', { status: 'ok' });
        } else {
          setSnackbar({ open: true, message: 'Failed to upload PDF before final approval', severity: 'error' });
          endSpan('ui_workflow_upload_pdf', { status: 'error' });
          endSpan('ui_workflow_export_pdf_before_approve', { status: 'error' });
          return;
        }
      }

      setLoading(false);
      // 21 CFR Part 11 §11.200 electronic signature — deferred until the PIN
      // dialog confirms; PDF export/upload above is preparation, not signing.
      openSignDialog('approved', async (signSessionId) => {
        const token = getToken();
        const response = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/coordinator-final-approve`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ signSessionId, meaning: 'approved' }),
        });
        if (response.ok) {
          setWorkflowState('approved');
          showSnackbar('Document approval finalized');
          handleNext();
          await loadWorkflowState();
        } else {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || 'Final approval failed');
        }
      });
    } catch (error) {
      console.error('Error finalizing approval:', error);
      showSnackbar('Error finalizing approval', 'error');
      setLoading(false);
    }
  };

  /**
   * Opens the shared electronic-signature dialog (ZohoSignDialog.jsx),
   * deferring the actual signing request until Zoho reports the signature
   * complete. `run` receives the signSessionId and should perform the
   * fetch, throwing on failure (the thrown message is shown in the dialog
   * so the user can retry without losing context).
   */
  const openSignDialog = (meaning, run) => {
    setSignError('');
    setSignAction({ meaning, run });
  };

  const closeSignDialog = () => {
    if (signSubmitting) return;
    setSignAction(null);
    setSignError('');
  };

  const handleSignConfirm = async (signSessionId) => {
    if (!signAction) return;
    setSignSubmitting(true);
    setSignError('');
    try {
      await signAction.run(signSessionId);
      setSignAction(null);
    } catch (e) {
      setSignError(e.message || 'Signing failed');
    } finally {
      setSignSubmitting(false);
    }
  };

  const recordApproverApproval = async () => {
    if (!documentId || documentId === 'new') return;
    openSignDialog('approved', async (signSessionId) => {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/approver-approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signSessionId, meaning: 'approved' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Could not record approval');
      }
      showSnackbar(data.message || 'Your approval has been recorded', 'success');
      await loadWorkflowState();
    });
  };

  const recordApproverApprovalWithComment = async () => {
    if (!documentId || documentId === 'new') return;
    const commentAtSubmit = approvalComment;
    setApproveWithCommentsOpen(false);
    openSignDialog('approved', async (signSessionId) => {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/approver-approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signSessionId, meaning: 'approved', comment: commentAtSubmit }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Could not record approval');
      }
      showSnackbar(data.message || 'Your approval has been recorded', 'success');
      setApprovalComment('');
      await loadWorkflowState();
    });
  };

  const markCoAuthorComplete = async () => {
    if (!documentId || documentId === 'new') return;
    try {
      setLoading(true);
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/co-author-complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showSnackbar(data.message || 'Could not mark co-authoring complete', 'error');
        return;
      }
      if (data.coordinatorEmailOutcome === 'sent') {
        showSnackbar('Co-authoring marked complete. Project coordinators were notified by email.', 'success');
      } else if (data.coordinatorEmailOutcome === 'no_recipients') {
        showSnackbar(
          'Co-authoring marked complete. No coordinator email addresses found — coordinators were not emailed.',
          'warning'
        );
      } else if (data.coordinatorEmailOutcome === 'failed') {
        showSnackbar(
          'Co-authoring marked complete. Email to coordinators failed — try again or check SMTP settings.',
          'warning'
        );
      } else {
        showSnackbar('Co-authoring marked complete', 'success');
      }
      await loadWorkflowState();
    } catch (e) {
      showSnackbar('Could not mark co-authoring complete', 'error');
    } finally {
      setLoading(false);
    }
  };

  const markReviewComplete = async () => {
    if (!documentId || documentId === 'new') return;
    openSignDialog('reviewed', async (signSessionId) => {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/review-complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signSessionId, meaning: 'reviewed' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Could not mark review complete');
      }
      if (data.coordinatorEmailOutcome === 'sent') {
        showSnackbar('Review marked complete. Project coordinators were notified by email.', 'success');
      } else if (data.coordinatorEmailOutcome === 'no_recipients') {
        showSnackbar(
          'Review marked complete. No coordinator email addresses found — coordinators were not emailed.',
          'warning'
        );
      } else if (data.coordinatorEmailOutcome === 'failed') {
        showSnackbar(
          'Review marked complete. Email to coordinators failed — try again or check SMTP settings.',
          'warning'
        );
      } else {
        showSnackbar('Review marked complete', 'success');
      }
      await loadWorkflowState();
    });
  };

  const coordinatorMarkFinal = async (dueDate) => {
    if (!documentId || documentId === 'new') return;
    if (!dueDate) {
      openDueDateDialog('approval');
      return;
    }
    try {
      setLoading(true);
      const { ok, data } = await postCoordinatorWorkflowAction('coordinator-mark-final', dueDate);
      if (!ok) {
        showSnackbar(data.message || 'Could not mark final', 'error');
        return;
      }
      setWorkflowState('pendingApproval');
      setWorkflowDueDate(data.dueDate || dueDate);
      showSnackbar(data.message || 'Sent for approval', 'success');
      closeDueDateDialog();
      await loadWorkflowState();
    } catch (e) {
      showSnackbar('Could not mark final', 'error');
    } finally {
      setLoading(false);
    }
  };

  const confirmDueDateDialog = async () => {
    const { action, dueDate } = dueDateDialog;
    if (!dueDate) {
      showSnackbar('Please select a due date', 'warning');
      return;
    }
    if (action === 'coAuthor') await sendForCoAuthoring(dueDate);
    else if (action === 'review') await sendForReview(dueDate);
    else if (action === 'approval') await coordinatorMarkFinal(dueDate);
  };

  const renderWorkflowActions = () => {
    const uid = uidStr(user);
    const userIsAssignedReviewer = memberOfWorkflow(user, reviewers);
    const userIsCoAuthor = memberOfWorkflow(user, coAuthors);
    const userIsCoordinator = memberOfWorkflow(user, coordinators);
    const userIsApprover = memberOfWorkflow(user, approvers);
    const reviewerExperience = userIsAssignedReviewer;

    const hasCompletedReview = (reviewProgress || []).some(
      (p) => p.completedAt && String(p.reviewer?._id ?? p.reviewer) === uid
    );

    const rejectDialog = (
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject Document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Comment"
            type="text"
            fullWidth
            multiline
            minRows={3}
            value={rejectionComment}
            onChange={(e) => setRejectionComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    );

    const reopenDialog = (
      <Dialog open={reopenDialogOpen} onClose={() => setReopenDialogOpen(false)}>
        <DialogTitle>Reopen Document for Amendment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This document was approved and its content is locked. Reopening it restarts the
            co-authoring, review, and approval cycle — prior signatures are withdrawn (not
            erased) and the approved record already on file is kept as-is.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for reopening"
            type="text"
            fullWidth
            required
            multiline
            minRows={3}
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReopenForAmendment}
            variant="contained"
            color="warning"
            disabled={!reopenReason.trim()}
          >
            Reopen for Amendment
          </Button>
        </DialogActions>
      </Dialog>
    );

    const approveCommentsDialog = (
      <Dialog open={approveWithCommentsOpen} onClose={() => setApproveWithCommentsOpen(false)}>
        <DialogTitle>Record approval with comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Comment (optional)"
            type="text"
            fullWidth
            multiline
            minRows={3}
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveWithCommentsOpen(false)}>Cancel</Button>
          <Button onClick={recordApproverApprovalWithComment} variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    );

    const myApproverEntry = (approverProgress || []).find(
      (p) => p.approvedAt && String(p.approver?._id ?? p.approver) === uid
    );
    const hasRecordedApproverApproval = Boolean(myApproverEntry);
    const canCoordinatorFinalize =
      userIsCoordinator && (allApproversMarked || approversAssigned === 0);

    if (workflowState === 'pendingApproval') {
      const approverSection =
        userIsApprover && approversAssigned > 0 ? (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            {!hasRecordedApproverApproval ? (
              <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={recordApproverApproval}>
                Record my approval
              </Button>
            ) : (
              <Chip
                label={
                  myApproverEntry
                    ? `${myApproverEntry.meaning || 'Approved'} by ${myApproverEntry.signedName || 'you'} — ${new Date(myApproverEntry.approvedAt).toLocaleString()}`
                    : 'Your approval is recorded'
                }
                color="success"
                variant="outlined"
              />
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ApproveIcon />}
              onClick={() => setApproveWithCommentsOpen(true)}
            >
              Record approval with comment
            </Button>
            <Button variant="outlined" color="error" startIcon={<ViewIcon />} onClick={() => setRejectDialogOpen(true)}>
              Reject
            </Button>
          </Box>
        ) : null;

      const coordinatorSection = userIsCoordinator ? (
        <Box sx={{ mb: 2 }}>
          {canCoordinatorFinalize ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={coordinatorFinalizeDocumentApproval}
            >
              Finalize document approval
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Waiting for all study approvers to record their approval.
            </Typography>
          )}
        </Box>
      ) : null;

      const observerNote =
        !userIsApprover || approversAssigned === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Approvers record sign-off first; then any project coordinator finalizes document approval.
          </Typography>
        ) : null;

      if (userIsApprover && approversAssigned > 0) {
        return (
          <Box sx={{ mt: 2 }}>
            {observerNote}
            {approverSection}
            {userIsCoordinator ? coordinatorSection : null}
            {approveCommentsDialog}
            {rejectDialog}
          </Box>
        );
      }

      if (userIsCoordinator) {
        return (
          <Box sx={{ mt: 2 }}>
            {observerNote}
            {coordinatorSection}
          </Box>
        );
      }

      return (
        <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
          This document is in approver sign-off. Coordinators finalize after all approvers record approval.
        </Typography>
      );
    }

    // Reviewer-specific actions only for these states; draft/coAuthoring fall through so authors with reviewer role still see Send for Co-Authoring / Send for Review.
    if (reviewerExperience && workflowState === 'reviewing') {
      return (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {!hasCompletedReview ? (
            <Button variant="contained" color="primary" onClick={markReviewComplete}>
              Mark my review complete
            </Button>
          ) : (
            <Chip label="Your review is complete" color="success" variant="outlined" />
          )}
          <Button variant="outlined" color="error" startIcon={<ViewIcon />} onClick={() => setRejectDialogOpen(true)}>
            Reject
          </Button>
          {userIsCoordinator && allReviewsComplete ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<SendIcon />}
              onClick={() => openDueDateDialog('approval')}
            >
              Mark final — send for approval
            </Button>
          ) : null}
          {rejectDialog}
        </Box>
      );
    }

    if (reviewerExperience && workflowState === 'approved') {
      return (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="success" startIcon={<ViewIcon />} onClick={() => setSelectedTab(3)}>
            View Final Document
          </Button>
        </Box>
      );
    }
    // Non-reviewers: original workflow actions
    switch (workflowState) {
      case 'draft':
        return userIsCoordinator ? (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => openDueDateDialog('coAuthor')}
            >
              Send for Co-Authoring
            </Button>
          </Box>
        ) : (
          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            Only a project coordinator can send this document for co-authoring.
          </Typography>
        );
      case 'coAuthoring': {
        const coAuthorsAssigned = (coAuthors || []).length;
        const hasCompletedCoAuthoring = (coAuthorProgress || []).some(
          (p) => p.completedAt && String(p.coAuthor?._id ?? p.coAuthor) === uid
        );
       const canSendForReview =
          userIsCoordinator && (allCoAuthorsComplete || coAuthorsAssigned === 0);

        const coAuthorSection =
          userIsCoAuthor && coAuthorsAssigned > 0 ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              {!hasCompletedCoAuthoring ? (
                <Button variant="contained" color="primary" onClick={markCoAuthorComplete}>
                  Mark my co-authoring complete
                </Button>
              ) : (
                <Chip label="Your co-authoring is complete" color="success" variant="outlined" />
              )}
            </Box>
          ) : null;

        const coordinatorSection = userIsCoordinator ? (
          <Box sx={{ mb: 2 }}>
            {canSendForReview ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => openDueDateDialog('review')}
                disabled={!pdfUrl}
              >
                Send for Review
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Waiting for all assigned co-authors to mark co-authoring complete.
              </Typography>
            )}
          </Box>
        ) : !userIsCoAuthor ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Co-authors mark their work complete; a project coordinator sends for review when everyone is done.
          </Typography>
        ) : null;
        return (
          <Box sx={{ mt: 2 }}>
            {coAuthorSection}
            {coordinatorSection}
          </Box>
        );
      }
      case 'reviewing':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reviewers on the study team mark their review complete here or via email link. When everyone has finished,
              project coordinators can mark final and notify approvers.
            </Typography>
            {userIsCoordinator && allReviewsComplete ? (
               <Button
                variant="contained"
                color="success"
                startIcon={<SendIcon />}
                onClick={() => openDueDateDialog('approval')}
              >
                Mark final — send for approval
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {userIsCoordinator
                  ? 'Waiting for all assigned reviewers to mark review complete.'
                  : 'Waiting for reviewers to complete. A project coordinator will send for approval when ready.'}
              </Typography>
            )}
          </Box>
        );
      case 'approved':
        return (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="contained" color="success" startIcon={<ViewIcon />} onClick={() => setSelectedTab(3)}>
                View Final Document
              </Button>
              {userIsCoordinator ? (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setReopenDialogOpen(true)}
                >
                  Reopen for Amendment
                </Button>
              ) : null}
            </Box>
            {reopenDialog}
          </Box>
        );
      default:
        return null;
    }
  };

  const renderCreateWorkflowForm = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Create New Document Workflow
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Create a document workflow. Co-authors and reviewers are managed under Study Registration → Study team and sync
          to this workflow automatically.
        </Typography>
        
        <Paper sx={{ p: 3, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Document Information
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Document Title"
              placeholder="Enter document title"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Document Type"
              placeholder="e.g., Protocol, Consent Form, Case Report"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              placeholder="Brief description of the document"
            />
          </Box>
          
          {/* <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            Workflow Setup
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => openUserDialog('coAuthors')}
              sx={{ mr: 2, mb: 2 }}
            >
              Assign Co-Authors
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => openUserDialog('reviewers')}
              sx={{ mb: 2 }}
            >
              Assign Reviewers
            </Button>
          </Box> */}
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button variant="contained" color="primary">
              Create Workflow
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  };

  // 1. Add the download handler function
  const handleDownloadWord = async () => {
    const token = getToken();
    // Try Word file first
    if (documentData?.azureBlobName) {
      try {
        const response = await fetch(`${API_BASE_URL}/documents/download/${documentData.azureBlobName}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          saveAs(blob, `${documentData?.title || 'document'}.docx`);
          return;
        }
      } catch (error) {
        // Continue to PDF fallback
      }
    }
    // Fallback: Try PDF
    if (pdfUrl) {
      try {
        const response = await fetch(pdfUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          saveAs(blob, `${documentData?.title || 'document'}.pdf`);
          showSnackbar('Downloaded as PDF (Word not available)', 'info');
          return;
        }
      } catch (error) {
        // Continue to error
      }
    }
    showSnackbar('Failed to download document as Word or PDF', 'error');
  };

  const handleReject = async () => {
    const commentAtSubmit = rejectionComment;
    setRejectDialogOpen(false);
    openSignDialog('rejected', async (signSessionId) => {
      const response = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ signSessionId, meaning: 'rejected', comment: commentAtSubmit })
      });
      if (response.ok) {
        setWorkflowState('coAuthoring');
        showSnackbar('Document sent back to co-authoring with rejection comment');
        setRejectionComment('');
        await loadWorkflowState();
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Failed to reject document');
      }
    });
  };

  const handleReopenForAmendment = async () => {
    const reasonAtSubmit = reopenReason.trim();
    if (!reasonAtSubmit) return;
    setReopenDialogOpen(false);
    openSignDialog('reopened', async (signSessionId) => {
      const response = await fetch(`${API_BASE_URL}/workflows/${documentId}/workflow/reopen-for-amendment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ signSessionId, meaning: 'reopened', reason: reasonAtSubmit })
      });
      if (response.ok) {
        setWorkflowState('coAuthoring');
        showSnackbar('Document reopened for amendment. Content is unlocked.');
        setReopenReason('');
        await loadWorkflowState();
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to reopen document for amendment');
      }
    });
  };

  // If this is a new document, show the create form
  if (isNewDocument) {
    return renderCreateWorkflowForm();
  }

  const protocolEditorLocked = workflowState === 'approved';

  const allTabs = [
    { label: 'Project Section Editor', icon: <EditIcon /> },
    //{ label: 'Editor', icon: <EditIcon /> },
    { label: 'Comments', icon: <CommentIcon /> },
    { label: 'History', icon: <HistoryIcon /> },
    { label: 'Preview', icon: <ViewIcon /> },
  ];

  // Assigned reviewers + reviewer role: hide Project Section Editor — Comments, History, Preview only
  // Assigned reviewers: Comments and Preview only (no Project Section Editor or History)
  const visibleTabs = workflowReviewerMode
    ? REVIEWER_TAB_INDICES.map((i) => allTabs[i])
    : allTabs;

  const getVisibleTabIndex = (tabIndex) => {
    if (workflowReviewerMode) {
      const idx = REVIEWER_TAB_INDICES.indexOf(tabIndex);
      return idx >= 0 ? idx : 0;
    }
    return tabIndex;
  };
  const getActualTabIndex = (visibleTabIndex) => {
    if (workflowReviewerMode) {
      return REVIEWER_TAB_INDICES[visibleTabIndex] ?? 1;
    }
    return visibleTabIndex;
  };


  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Document Workflow
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* {user?.role === 'reviewer' ? ( */}
            <Chip
              label={
              workflowReviewerMode
                ? `${workflowStates[workflowState]?.label || workflowState || 'Workflow'} · Review`
                : workflowStates[workflowState]?.label || workflowState || 'Workflow'
            }
            color={workflowReviewerMode ? 'secondary' : workflowStates[workflowState]?.color}
            icon={
              workflowReviewerMode
                ? <CommentIcon />
                : workflowStates[workflowState]?.icon
                  ? React.createElement(workflowStates[workflowState].icon)
                  : undefined
            }
            />
          {/* // ) : (
          // <Chip
          //   label={workflowStates[workflowState]?.label}
          //   color={workflowStates[workflowState]?.color}
          //   icon={workflowStates[workflowState]?.icon ? React.createElement(workflowStates[workflowState].icon) : undefined}
          // />
          // )} */}
          <Typography variant="body2" color="text.secondary">
            {documentData?.title || 'Untitled Document'}
          </Typography>
          <Button
            component={RouterLink}
            to={`/guidance/protocol-review?role=${guidanceRole}`}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
          >
            Protocol Review Guidance
          </Button>
        </Box>
      </Box>

      {/* Workflow Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} orientation="horizontal">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                optional={
                  index === 4 ? (
                    <Typography variant="caption">Final step</Typography>
                  ) : null
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Document Editor */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={getVisibleTabIndex(selectedTab)}
                onChange={(e, newValue) => setSelectedTab(getActualTabIndex(newValue))}
              >
                {visibleTabs.map((tab) => (
                  <Tab key={tab.label} label={tab.label} icon={tab.icon} />
                ))}
              </Tabs>
            </Box>

            {/* Tab Content */}
            {selectedTab === 0 && !workflowReviewerMode && (
              <Box>
                 {protocolEditorLocked && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This document is approved. The protocol cannot be edited here; use Preview or download if available.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    //disabled={workflowState === 'approved'}
                    disabled={protocolEditorLocked}
                    onClick={async () => {
                      if (workflowState === 'approved') {
                        setSnackbar({ open: true, message: 'Document is approved and locked', severity: 'info' });
                        return;
                      }
                      if (documentEditorRef.current && documentEditorRef.current.exportToPdf) {
                        pdfProbeAbortRef.current?.abort();
                        startSpan('ui_workflow_export_pdf_save', { page: 'workflow', meta: { documentId } });
                        const pdfBlob = await documentEditorRef.current.exportToPdf();
                        const formData = new FormData();
                        formData.append('pdf', pdfBlob, 'document.pdf');
                        const token = getToken();
                        startSpan('ui_workflow_upload_pdf', { page: 'workflow', meta: { documentId } });
                        const response = await fetch(`${API_BASE_URL}/documents/${documentId}/upload-pdf`, {
                          method: 'POST',
                          body: formData,
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          setPdfUrl(`${API_BASE_URL}/documents/${documentId}/pdf`);
                          setSnackbar({ open: true, message: 'Document saved successfully', severity: 'success' });
                          endSpan('ui_workflow_upload_pdf', { status: 'ok' });
                          endSpan('ui_workflow_export_pdf_save', { status: 'ok' });
                        } else {
                          setSnackbar({ open: true, message: 'Failed to upload PDF', severity: 'error' });
                          endSpan('ui_workflow_upload_pdf', { status: 'error' });
                          endSpan('ui_workflow_export_pdf_save', { status: 'error' });
                        }
                        } else {
                        setSnackbar({
                          open: true,
                          message:
                            'Editor is not ready to export PDF yet. Wait for the document to finish loading, then try Save again.',
                          severity: 'warning',
                        });
                      }
                    }}
                  >
                    Save
                  </Button>
                </Box>
                {workflowState === 'approved' ? (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This document is approved and locked. Editing is disabled.
                    </Alert>
                    {pdfUrl ? (
                      <PdfJsSimpleViewer pdfUrl={pdfUrl} />
                    ) : (
                      <Typography color="text.secondary">
                        No exported PDF available to preview. (Editing is still locked after approval.)
                      </Typography>
                    )}
                  </Box>
                ) : (
                   <DocumentEditor
                    ref={documentEditorRef}
                    projectId={workflow?.projectId}
                    protocolEditingDisabled={protocolEditorLocked}
                    workflowReviewToolbar
                  />
                )}
              </Box>
            )}

            {selectedTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {/* Document Editor
                </Typography>
                {workflowState === 'approved' ? (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This document is approved and locked. The editor is disabled.
                    </Alert>
                    {pdfUrl ? (
                      <PdfJsSimpleViewer pdfUrl={pdfUrl} />
                    ) : (
                      <Typography color="text.secondary">
                        No exported PDF available to preview. (Editing is still locked after approval.)
                      </Typography>
                    )}
                  </Box>
                ) : (
                  renderOnlyOfficeEditor()
                )}
              </Box>
            )}

            {selectedTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom> */}
                  Review and Comment
                </Typography>
                {/* PDF.js Highlight Viewer */}
                {pdfUrl ? (
                  <PdfJsHighlightViewer
                    pdfId={documentId}
                    pdfUrl={pdfUrl}
                    readOnly={workflowState === 'approved'}
                  />
                ) : (
                  <div style={{ color: '#888', marginBottom: 16 }}>No exported PDF available. Please save in Project Section Editor tab first.</div>
                )}
              </Box>
            )}

            {selectedTab === 2 && !workflowReviewerMode && (
              <Box>
                {((reviewProgress || []).length > 0 || (approverProgress || []).length > 0) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Signatures
                    </Typography>
                    {/* 21 CFR Part 11 §11.50 manifestation — printed name, meaning, and
                        date/time of each electronic signature, shown on every view of
                        the record (not just to the signer). Data captured server-side
                        in documentWorkflow.routes.js at review-complete/approver-approve. */}
                    <List dense>
                      {[...(reviewProgress || []), ...(approverProgress || [])]
                        .filter((entry) => entry.meaning)
                        .sort((a, b) => new Date(a.completedAt || a.approvedAt) - new Date(b.completedAt || b.approvedAt))
                        .map((entry, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${entry.signedName || 'Unknown signer'} — ${entry.meaning}`}
                              secondary={new Date(entry.completedAt || entry.approvedAt).toLocaleString()}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Box>
                )}
                <Typography variant="h6" gutterBottom>
                  Document History
                </Typography>
                <List>
                  {documentHistory.map((entry, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={entry.action}
                        secondary={
                          entry.metadata?.signedName
                            ? `${formatWorkflowHistoryUser(entry.user)} — ${new Date(entry.timestamp).toLocaleString()} · signed as "${entry.metadata.meaning}" by ${entry.metadata.signedName}`
                            : `${formatWorkflowHistoryUser(entry.user)} — ${new Date(entry.timestamp).toLocaleString()}`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {selectedTab === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Document Preview
                </Typography>
                {pdfUrl ? (
                  <>
                    <PdfJsSimpleViewer pdfUrl={pdfUrl} />
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleDownloadWord}
                      >
                        Download as Word
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography>No exported PDF available. Please save in Project Section Editor tab first.</Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Workflow Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Workflow Actions
            </Typography>
            {workflowDueDate ? (
              <Chip
                size="small"
                label={`Current due date: ${new Date(workflowDueDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}`}
                sx={{ mb: 1 }}
              />
            ) : null}
            {renderWorkflowActions()}

            <Divider sx={{ my: 2 }} />

            {/* Co-Authors */}
            <Typography variant="subtitle1" gutterBottom>
              Co-Authors
            </Typography>
            <List dense>
              {coAuthors.map((rawAuthor, index) => {
                const author = resolveUser(rawAuthor);
                return (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      title="Reassign"
                      onClick={() => openReassignDialog(author, ['co_authors', 'author_admins'])}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                  <Avatar>{getUserInitials(author)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getUserFullName(author)}
                    secondary={author?.email || author?.role || null}
                  />
                </ListItem>
              )})}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Reviewers */}
            <Typography variant="subtitle1" gutterBottom>
              Reviewers
            </Typography>
            <List dense>
              {reviewers.map((rawReviewer, index) => {
                const reviewer = resolveUser(rawReviewer);
                return (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      title="Reassign"
                      onClick={() => openReassignDialog(reviewer, ['reviewers'])}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                  <Avatar>{getUserInitials(reviewer)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getUserFullName(reviewer)}
                    secondary={reviewer?.email || reviewer?.role || null}
                  />
                </ListItem>
              )})}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Coordinators */}
            <Typography variant="subtitle1" gutterBottom>
              Coordinators
            </Typography>
            <List dense>
              {coordinators.map((rawCoordinator, index) => {
                const coordinator = resolveUser(rawCoordinator);
                return (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      title="Reassign"
                      onClick={() => openReassignDialog(coordinator, ['coordinators'])}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                  <Avatar>{getUserInitials(coordinator)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getUserFullName(coordinator)}
                    secondary={coordinator?.email || coordinator?.role || null}
                  />
                </ListItem>
              )})}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Approvers */}
            <Typography variant="subtitle1" gutterBottom>
              Approvers
            </Typography>
            <List dense>
              {approvers.map((rawApprover, index) => {
                const approver = resolveUser(rawApprover);
                return (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      title="Reassign"
                      onClick={() => openReassignDialog(approver, ['approvers'])}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                  <Avatar>{getUserInitials(approver)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getUserFullName(approver)}
                    secondary={approver?.email || approver?.role || null}
                  />
                </ListItem>
              )})}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <ReassignPersonDialog
        open={reassignState.open}
        onClose={closeReassignDialog}
        projectId={studyProjectId}
        roleKey={reassignState.role}
        roleLabel={ROLE_LABELS[reassignState.role] || ''}
        currentPerson={reassignState.person}
        occupiedKeys={reassignOccupiedKeys}
        onConfirm={handleReassignConfirm}
      />

      <ZohoSignDialog
        open={!!signAction}
        meaning={signAction?.meaning}
        documentId={String(documentId)}
        onClose={closeSignDialog}
        onConfirm={handleSignConfirm}
        error={signError}
        submitting={signSubmitting}
      />

      <Dialog open={dueDateDialog.open} onClose={closeDueDateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {ASSIGN_DUE_DATE_CONFIG[dueDateDialog.action]?.title || 'Assign due date'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {ASSIGN_DUE_DATE_CONFIG[dueDateDialog.action]?.description ||
              'Assign a due date for the assigned team members. This date appears in workflow notification emails.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Due date"
            type="date"
            value={dueDateDialog.dueDate}
            onChange={(e) => setDueDateDialog((prev) => ({ ...prev, dueDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: getTodayDateInputMin() }}
            helperText="Recipients must complete their action by this date (included in email subject and body)."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDueDateDialog}>Cancel</Button>
          <Button
            onClick={confirmDueDateDialog}
            variant="contained"
            color="primary"
            disabled={!dueDateDialog.dueDate || loading}
          >
            {ASSIGN_DUE_DATE_CONFIG[dueDateDialog.action]?.confirmLabel || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OnlyOfficeWorkflow; 